import { isNonIndiaOrg } from '../../../utils/appHelper';
import * as Yup from 'yup';

// Middle East local number formats (no country code):
// 8 digits  – Kuwait, Bahrain, Qatar, Oman, Lebanon (e.g. 55123456)
// 9 digits  – UAE landline (e.g. 044123456)
// 10 digits – UAE/Saudi/Jordan mobile (e.g. 0501234567)
// 11 digits – Egypt mobile (e.g. 01012345678)
const MIDDLE_EAST_PHONE_REGEX = /^0?[1-9][0-9]{7,9}$/;

const MIDDLE_EAST_CITY_IDS = [1001, 1003, 1004, 1005, 1006];

export const staffValidationSchema = (data) => {
    const isBlueDart = !!data?.isBlueDartOrg;
    const isEdit = !!data?.isEdit;
    const isMiddleEast =
        isNonIndiaOrg() || MIDDLE_EAST_CITY_IDS.includes(data?.operatingCityId);
    const staffTypes = data?.staffTypes ?? [];
    const isInHouseStaffId = (staffTypeId) => {
        const staffType = staffTypes.find(
            (t) => String(t.id) === String(staffTypeId),
        );
        return !!staffType?.name?.toLowerCase()?.includes('in house');
    };
    return Yup.object().shape({
        name: Yup.string()
            .required('Staff Name is required')
            .when([], {
                is: () => isBlueDart,
                // BD Oracle CEMPLNAME VARCHAR2(30) — keep in sync with BluedartConstants.BD_EMPLOYEE_NAME_MAX_LENGTH
                then: Yup.string().max(30, 'Staff Name cannot exceed 30 characters'),
                otherwise: Yup.string(),
            }),
        contactNumber: Yup.string()
            .required('Phone Number is required')
            .test(
                'valid-phone',
                isMiddleEast
                    ? 'Please enter a valid phone number (8 to 11 digits)'
                    : 'Phone number must be 10 digits',
                function (value) {
                    if (!value) return false;
                    // Bypass format check only in edit mode when the number is masked
                    // (flag from BE or regex on value). In create mode, always validate format.
                    if (isEdit && (this.parent.maskDetails || /^X+\d+$/i.test(value))) return true;
                    const cleaned = value.replace(/[\s\-().]/g, '');
                    if (isMiddleEast) {
                        return MIDDLE_EAST_PHONE_REGEX.test(cleaned);
                    }
                    return /^[0-9]{10}$/.test(cleaned);
                },
            ),
        dateOfBirthEpochMillis: Yup.date()
            .transform((value, originalValue) => {
                if (originalValue == null || originalValue === '') return null;
                const timestamp = Number(originalValue);
                if (isNaN(timestamp)) return null;
                return new Date(timestamp);
            })
            .typeError('Date of Birth is required')
            .max(new Date(), 'Date of Birth cannot be in the future')
            .required('Date of Birth is required'),
        joiningDateEpochMillis: Yup.date()
            .transform((value, originalValue) => {
                return originalValue ? new Date(Number(originalValue)) : null;
            })
            .typeError('Invalid date format')
            .required('Date of Join is required'),
        staffTypeId: Yup.string().required('Staff Type is required'),
        // In House Staff (name contains 'in house') → MOT/Vehicle Type not required
        // Delivery Boy or no staff type → required
        // Error message is "MOT is required" for BlueDart, "Vehicle Type is required" otherwise
        vehicleTypeId: Yup.mixed().when('staffTypeId', {
            is: (staffTypeId) => !isInHouseStaffId(staffTypeId),
            then: Yup.number().required(
                isBlueDart ? 'MOT is required' : 'Vehicle Type is required',
            ),
            otherwise: Yup.mixed().notRequired(),
        }),
        vehicleNumber: Yup.string().when('vehicleTypeId', {
            is: (vehicleTypeId) => !!vehicleTypeId?.vehicleNumberMandatory,
            then: () => Yup.string().required('Vehicle Number is required'),
            otherwise: () => Yup.string(),
        }),
        selfieEnabled: Yup.boolean(),
        trackAndTraceEnabled: Yup.boolean(),
        geofenceAutoApproval: Yup.boolean(),
        hubId: Yup.string().nullable(),
        shiftTimingIds: Yup.array().when([], {
            is: () => !data?.isDedicatedSlotEnabled && !data?.removeShift, // if dedicated slot is NOT enabled and removeShift is NOT enabled
            then: Yup.array()
                .min(
                    1,
                    'At least one shift timing must be selected  on WorkInfo Tab',
                )
                .required('Shift Timing is required'),
            otherwise: Yup.array().notRequired(),
        }),
        zones: Yup.array(),
        trainingCompleted: Yup.boolean(),
        freelancer: Yup.boolean(),
        // Salary Info tab is disabled for Blue Dart — nothing on it is validated
        salaryType: isBlueDart
            ? Yup.string()
            : Yup.string().required('Salary Type is required').default('4'),
        salaryCycle: Yup.string(),
        salaryCycleDay: Yup.string().when('salaryCycle', {
            is: (salaryCycle) => !isBlueDart && salaryCycle === 'monthly',
            then: Yup.string().required('Salary Cycle Day is required'),
        }),
        baseRate: Yup.number(),
        baseRateType: Yup.string(),
        openingBalance: Yup.number(),
        staffCustCode: Yup.string(),
        vendorId: Yup.string(),
        customerId: Yup.string(),
        // BD has no Other Info section — its mapping is auto-created with the
        clients: isBlueDart
            ? Yup.array()
            : Yup.array().of(
                  Yup.object().shape({
                      customerId: Yup.string().required('Client is required'),
                      staffCustCode: Yup.string().required(
                          'Staff Customer Code is required',
                      ),
                  }),
              ),
        // BD hides the "Source of Delivery Boy" section entirely, so it is only
        // mandatory for every other org.
        userCreationSource: Yup.string().when([], {
            is: () => !isBlueDart,
            then: Yup.string().required('Source of Delivery Boy is required'),
            otherwise: Yup.string(),
        }),
        userCreationSourceId: Yup.string(),
        sourceRemarks: Yup.string(),
        // Blue Dart specific fields — sccCode, employeeMode, interviewTaken, documentValidation,
        // gender, educationalQualification, emailId, rollCode, shirtSize, trouserSize, shoeSize are mandatory
        gender: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Gender is required'),
            otherwise: Yup.string(),
        }),
        sccCode: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Service Centre is required'),
            otherwise: Yup.string(),
        }),
        employeeMode: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Employee Mode is required'),
            otherwise: Yup.string(),
        }),
        rollCode: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Roll Code is required'),
            otherwise: Yup.string(),
        }),
        interviewTaken: Yup.boolean().when([], {
            is: () => isBlueDart && !isEdit,
            then: Yup.boolean()
                .oneOf([true], 'Interview must be completed before onboarding')
                .required('Interview Taken is required'),
            otherwise: Yup.boolean(),
        }),
        documentValidation: Yup.boolean().when([], {
            is: () => isBlueDart && !isEdit,
            then: Yup.boolean()
                .oneOf(
                    [true],
                    'Document validation must be completed before onboarding',
                )
                .required('Document Validation is required'),
            otherwise: Yup.boolean(),
        }),
        educationalQualification: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Educational Qualification is required'),
            otherwise: Yup.string(),
        }),
        emailId: Yup.string().when([], {
            is: () => isBlueDart,
            // BD Oracle CEMAILID VARCHAR2(30) — keep in sync with BluedartConstants.BD_EMAIL_MAX_LENGTH.
            then: Yup.string()
                .email('Please enter a valid email address')
                .max(30, 'Email ID cannot exceed 30 characters')
                .required('Email ID is required'),
            otherwise: Yup.string(),
        }),
        uniformIssueDate: Yup.mixed(),
        shirtSize: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Shirt Size is required'),
            otherwise: Yup.string(),
        }),
        trouserSize: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Trouser Size is required'),
            otherwise: Yup.string(),
        }),
        shoeSize: Yup.string().when([], {
            is: () => isBlueDart,
            then: Yup.string().required('Shoe Size is required'),
            otherwise: Yup.string(),
        }),
    });
};
